<template>
  <div>
    <div class="max-w-7xl mx-auto">
      <!-- Centered Loading State -->
      <div
        v-if="loading"
        class="fixed inset-0 bg-white/80 backdrop-blur-sm flex justify-center items-center z-50"
      >
        <div class="text-center">
          <div
            class="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"
          ></div>
          <p class="text-lg font-semibold text-gray-700">
            Loading leave requests...
          </p>
        </div>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
      >
        <div class="flex items-center">
          <svg
            class="w-5 h-5 text-red-500 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
          <p class="text-red-700 font-medium">{{ error }}</p>
        </div>
        <button
          @click="fetchLeaveRequests"
          class="mt-2 text-red-600 hover:text-red-800 font-medium"
        >
          Try Again
        </button>
      </div>

      <!-- Filters -->
      <div
        class="backdrop-blur-sm rounded-2xl border p-6 mb-8 bg-gray-600 border-gray-700 shadow-lg"
      >
        <!-- Header with Title + Hamburger Button (mobile only) -->
        <div class="flex items-center justify-between gap-3 mb-4">
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 bg-blue-900 rounded-lg flex items-center justify-center"
            >
              <svg
                class="w-4 h-4 text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
            </div>
            <h2 class="text-lg font-semibold text-gray-100">Filter Requests</h2>
          </div>

          <!-- Toggle button for mobile -->
          <button
            @click="showMobileFilters = !showMobileFilters"
            class="md:hidden p-2 rounded-lg hover:bg-gray-700 transition-colors"
            :class="{ 'bg-gray-700': showMobileFilters }"
          >
            <svg
              class="w-6 h-6 text-gray-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>

        <!-- Filter Inputs -->
        <div
          :class="{
            'hidden md:grid': !showMobileFilters,
            'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4': true,
          }"
          class="transition-all duration-300"
        >
          <div class="space-y-2">
            <label class="text-sm font-medium text-gray-300">Search</label>
            <div class="relative">
              <svg
                class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                v-model="filters.search"
                type="text"
                placeholder="Search by name or reason..."
                class="w-full pl-10 pr-4 py-2.5 bg-gray-700 text-gray-100 border border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                @input="debouncedSearch"
              />
            </div>
          </div>

          <div class="space-y-2">
            <label class="text-sm font-medium text-gray-300">Leave Type</label>
            <select
              v-model="filters.leaveType"
              class="w-full px-4 py-2.5 bg-gray-700 text-gray-100 border border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              @change="fetchLeaveRequests"
            >
              <option value="">All Types</option>
              <option value="Casual">Casual</option>
              <option value="Sick">Sick</option>
              <option value="Personal">Personal</option>
              <option value="Maternity">Maternity</option>
              <option value="Paternity">Paternity</option>
              <option value="Unpaid">Unpaid</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div class="space-y-2">
            <label class="text-sm font-medium text-gray-300">Month</label>
            <select
              v-model="filters.month"
              class="w-full px-4 py-2.5 bg-gray-700 text-gray-100 border border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              @change="fetchLeaveRequests"
            >
              <option value="">All Months</option>
              <option
                v-for="(month, index) in months"
                :key="index"
                :value="index + 1"
              >
                {{ month }}
              </option>
            </select>
          </div>

          <div class="space-y-2">
            <label class="text-sm font-medium text-gray-300">Year</label>
            <select
              v-model="filters.year"
              class="w-full px-4 py-2.5 bg-gray-700 text-gray-100 border border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              @change="fetchLeaveRequests"
            >
              <option value="">All Years</option>
              <option
                v-for="year in years"
                :key="year"
                :value="year"
              >
                {{ year }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <!-- Pending Review Card -->
        <div
          class="bg-white rounded-2xl p-4 border shadow-sm hover:shadow-md transition-shadow duration-200 text-sm"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="font-medium text-gray-600 mb-1">Pending Review</p>
              <p class="font-bold text-xl text-gray-900">
                {{ leaveSummary.totalPending }}
              </p>
            </div>
            <div
              class="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-5 h-5 text-amber-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
          <div class="mt-3 h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              class="h-full bg-amber-500 rounded-full"
              style="width: 60%"
            ></div>
          </div>
        </div>

        <!-- Approved Card -->
        <div
          class="bg-white rounded-2xl p-4 border shadow-sm hover:shadow-md transition-shadow duration-200 text-sm"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="font-medium text-gray-600 mb-1">Approved</p>
              <p class="font-bold text-xl text-gray-900">
                {{ leaveSummary.totalApproved }}
              </p>
            </div>
            <div
              class="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-5 h-5 text-emerald-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
          <div class="mt-3 h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              class="h-full bg-emerald-500 rounded-full"
              style="width: 80%"
            ></div>
          </div>
        </div>

        <!-- Declined Card -->
        <div
          class="bg-white rounded-2xl p-4 border shadow-sm hover:shadow-md transition-shadow duration-200 text-sm"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="font-medium text-gray-600 mb-1">Declined</p>
              <p class="font-bold text-xl text-gray-900">
                {{ leaveSummary.totalDeclined }}
              </p>
            </div>
            <div
              class="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-5 h-5 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </div>
          <div class="mt-3 h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              class="h-full bg-red-500 rounded-full"
              style="width: 20%"
            ></div>
          </div>
        </div>
      </div>

      <!-- Leave Request Cards -->
      <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        <div
          v-for="request in sortedLeaveRequests"
          :key="request._id"
          class="group bg-white/80 backdrop-blur-sm rounded-xl shadow-md shadow-slate-200/40 border transition-all duration-300 hover:-translate-y-1 overflow-hidden gap-4 flex flex-col justify-between h-full"
          :class="{
            'ring-2 ring-amber-200 bg-amber-50/50':
              request.status === 'Pending',
          }"
        >
          <!-- Card Header and Leave Details -->
          <div class="p-4 pb-3 relative">
            <div class="flex items-center">
              <div
                class="w-10 h-10 rounded-xl flex items-center justify-center shadow-md overflow-hidden flex-shrink-0"
              >
                <!-- If Photo Exists -->
                <img
                  v-if="request.user_id?.photo"
                  :src="request.user_id.photo"
                  :alt="request.user_id?.name"
                  class="w-10 h-10 object-cover rounded-xl"
                />

                <!-- If No Photo -->
                <div
                  v-else
                  class="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-800 text-white flex items-center justify-center font-bold text-sm rounded-xl"
                >
                  {{ getInitials(request.user_id?.name) }}
                </div>
              </div>

              <div class="ml-3">
                <h3 class="text-lg font-bold text-slate-900">
                  {{ request.user_id.name }}
                </h3>
                <p
                  class="flex items-center text-xs text-slate-500 mt-0.5 truncate"
                >
                  <svg
                    class="w-5 h-5 mr-1 text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5.121 17.804A13.937 13.937 0 0112 15c2.485 0 4.82.635 6.879 1.804M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  {{ request.user_id.role }}
                  <span class="mx-2">|</span>
                  <svg
                    class="w-5 h-5 mr-1 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m0 0a2 2 0 00-2-2H5a2 2 0 00-2 2m18 0v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8"
                    />
                  </svg>
                  {{ request.user_id.email }}
                </p>
              </div>
            </div>

            <!-- Status + 3-dot menu at Top Right -->
            <div class="absolute top-4 right-4 flex items-center gap-2">
              <span
                :class="[
                  'px-3 py-1 text-xs font-semibold rounded-lg shadow-sm whitespace-nowrap',
                  getStatusClass(request.status),
                ]"
              >
                {{ request.status }}
              </span>

              <div
                v-if="request.status !== 'Pending'"
                class="relative menu-container"
              >
                <button @click.stop="toggleMenu(request._id)">
                  <EllipsisVertical class="w-5 h-5 text-gray-600" />
                </button>

                <div
                  v-if="openMenu === request._id"
                  class="absolute right-0 mt-2 w-40 bg-white border rounded-lg shadow-lg z-50"
                >
                  <button
                    @click="undoStatus(request._id)"
                    :disabled="updatingStatus === request._id"
                    class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Undo Decision
                  </button>
                </div>
              </div>
            </div>

            <!-- Leave Info unchanged -->
            <div class="space-y-2 mt-8">
              <div
                class="flex items-center text-xs text-slate-700 bg-slate-50/80 rounded-lg p-2"
              >
                <div class="p-1 bg-emerald-100 rounded-lg mr-2">
                  <svg
                    class="w-3.5 h-3.5 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <span class="font-medium">Type: {{ request.leave_type }}</span>
                <span class="mx-2">|</span>
                <svg
                  class="w-3.5 h-3.5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>&nbsp;&nbsp;</span>
                <span class="font-medium"
                  >Duration: {{ request.duration_days }}</span
                >
              </div>

              <div
                class="flex items-center text-xs text-slate-700 bg-slate-50/80 rounded-lg p-2"
              >
                <div class="p-1 bg-blue-100 rounded-lg mr-2">
                  <svg
                    class="w-3.5 h-3.5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <span class="font-medium">{{
                  formatDateRange(request.from_datetime, request.to_datetime)
                }}</span>
              </div>
            </div>

            <div class="mt-4">
              <div
                class="bg-gradient-to-r from-slate-50 to-slate-100/50 p-3 rounded-lg border border-slate-200/50"
              >
                <p class="text-xs font-semibold text-slate-600 mb-1">Reason:</p>
                <p class="text-xs text-slate-700 leading-relaxed truncate">
                  {{ request.reason }}
                </p>
              </div>
            </div>
          </div>

          <!-- Action Buttons (unchanged) -->
          <div class="px-4 pb-4 mt-auto">
            <template v-if="request.status === 'Pending'">
              <div class="flex space-x-3">
                <!-- Approve Button -->
                <button
                  @click="updateStatus(request._id, 'Approved')"
                  :disabled="updatingStatus === request._id"
                  class="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 flex items-center justify-center shadow-md shadow-emerald-500/20 hover:shadow-emerald-500/30"
                >
                  <svg
                    v-if="updatingStatus === request._id"
                    class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <svg
                    v-else
                    class="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  Approve
                </button>

                <!-- Decline Button -->
                <button
                  @click="updateStatus(request._id, 'Declined')"
                  :disabled="updatingStatus === request._id"
                  class="flex-1 bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 flex items-center justify-center shadow-md shadow-rose-500/20 hover:shadow-rose-500/30"
                >
                  <svg
                    v-if="updatingStatus === request._id"
                    class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <svg
                    v-else
                    class="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                  Decline
                </button>
              </div>
            </template>

            <template v-else>
              <div
                class="bg-gradient-to-r from-slate-50 to-slate-100/50 p-3 rounded-lg border border-slate-200/50"
              >
                <p class="text-xs font-semibold text-slate-600 mb-1">
                  Processed on:
                </p>
                <p class="text-xs text-slate-700 font-medium">
                  {{ formatDate(request.processed_on) }}
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div
        v-if="pagination.totalPages > 1"
        class="flex justify-center items-center space-x-2 mt-8"
      >
        <button
          @click="changePage(pagination.page - 1)"
          :disabled="pagination.page === 1 || loading"
          class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>

        <span class="px-3 py-2 text-sm text-gray-700">
          Page {{ pagination.page }} of {{ pagination.totalPages }}
        </span>

        <button
          @click="changePage(pagination.page + 1)"
          :disabled="pagination.page === pagination.totalPages || loading"
          class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>

      <!-- Empty State -->
      <div
        v-if="!loading && leaveRequests.length === 0"
        class="text-center py-20 rounded-2xl border-2 border-dashed border-slate-300/60 bg-gradient-to-br from-slate-50/50 to-white"
      >
        <div
          class="w-20 h-20 bg-gradient-to-br from-slate-200 to-slate-300 rounded-2xl flex items-center justify-center mx-auto mb-6"
        >
          <svg
            class="w-10 h-10 text-slate-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            ></path>
          </svg>
        </div>
        <h3 class="text-xl font-black text-slate-900 mb-2">
          No leave requests found
        </h3>
        <p class="text-slate-600 font-medium">
          No leave requests match the selected filter criteria.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch, onBeforeUnmount } from "vue";
  import { useStore } from "vuex";
  import { EllipsisVertical } from "lucide-vue-next";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  const { showToast } = useGlobalToast();

  const store = useStore();

  // Reactive data
  const loading = ref(false);
  const error = ref(null);
  const updatingStatus = ref(null);
  const searchTimeout = ref(null);
  const showMobileFilters = ref(false);
  const openMenu = ref(null);

  const toggleMenu = (id) => {
    openMenu.value = openMenu.value === id ? null : id;
  };
  // Close menu when clicking outside
  const handleClickOutside = (event) => {
    // Check if the click is inside a menu
    if (!event.target.closest(".menu-container")) {
      openMenu.value = null;
    }
  };

  onMounted(() => {
    document.addEventListener("click", handleClickOutside);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("click", handleClickOutside);
  });

  const filters = ref({
    search: "",
    leaveType: "",
    month: "",
    year: new Date().getFullYear().toString(),
  });

  const pagination = ref({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // Static data
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = computed(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  });

  // Computed properties
  const leaveRequests = computed(() => store.getters.getLeaveRequests);
  const leaveSummary = computed(() => store.getters.getLeaveSummary);

  // Sort leave requests to show pending ones first
  const sortedLeaveRequests = computed(() => {
    const requests = [...leaveRequests.value];
    return requests.sort((a, b) => {
      // Pending requests come first
      if (a.status === "Pending" && b.status !== "Pending") return -1;
      if (a.status !== "Pending" && b.status === "Pending") return 1;

      // Within same status, sort by creation date (newest first)
      return (
        new Date(b.created_at || b.createdAt) -
        new Date(a.created_at || a.createdAt)
      );
    });
  });

  // Methods
  const fetchLeaveRequests = async (resetPage = true) => {
    if (resetPage) {
      pagination.value.page = 1;
    }

    loading.value = true;
    error.value = null;

    try {
      const result = await store.dispatch("fetchMyLeaves", {
        page: pagination.value.page,
        limit: pagination.value.limit,
        search: filters.value.search,
        leave_type: filters.value.leaveType,
        month: filters.value.month,
        year: filters.value.year,
      });

      if (result && result.pagination) {
        pagination.value = result.pagination;
      }
    } catch (err) {
      error.value = "Failed to fetch leave requests. Please try again.";
      showToast("error", "Failed to fetch leave requests. Please try again.");
      console.error("Error fetching leave requests:", err);
    } finally {
      loading.value = false;
    }
  };

  const debouncedSearch = () => {
    if (searchTimeout.value) {
      clearTimeout(searchTimeout.value);
    }

    searchTimeout.value = setTimeout(() => {
      fetchLeaveRequests(true);
    }, 500);
  };

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.value.totalPages) {
      pagination.value.page = newPage;
      fetchLeaveRequests(false);
    }
  };

  const updateStatus = async (leaveId, status) => {
    updatingStatus.value = leaveId;

    try {
      await store.dispatch("updateLeaveStatus", { leaveId, status });
      await fetchLeaveRequests(false); // Refresh current page
      showToast(
        "success",
        `Leave request ${status.toLowerCase()} successfully!`,
      );
    } catch (err) {
      error.value = `Failed to ${status.toLowerCase()} leave request. Please try again.`;

      showToast(
        "error",
        `Failed to ${status.toLowerCase()} leave request. Please try again.`,
      );
      console.error("Error updating leave status:", err);
    } finally {
      updatingStatus.value = null;
    }
  };

  // New undo functionality
  const undoStatus = async (leaveId) => {
    updatingStatus.value = leaveId;

    try {
      await store.dispatch("updateLeaveStatus", { leaveId, status: "Pending" });
      await fetchLeaveRequests(false); // Refresh current page
      showToast("success", "Leave request reverted to Pending successfully!");
    } catch (err) {
      error.value = "Failed to undo decision. Please try again.";
      showToast("error", "Failed to undo decision. Please try again.");
      console.error("Error undoing leave status:", err);
    } finally {
      updatingStatus.value = null;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "Pending":
        return "bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 border border-amber-200";
      case "Approved":
        return "bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-800 border border-emerald-200";
      case "Declined":
        return "bg-gradient-to-r from-rose-100 to-red-100 text-rose-800 border border-rose-200";
      default:
        return "bg-gradient-to-r from-slate-100 to-gray-100 text-slate-800 border border-slate-200";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateRange = (startDate, endDate) => {
    const start = formatDate(startDate);
    const end = formatDate(endDate);
    return startDate === endDate ? start : `${start} - ${end}`;
  };

  // Lifecycle
  onMounted(() => {
    fetchLeaveRequests();
  });

  // Watchers
  watch(
    [
      () => filters.value.leaveType,
      () => filters.value.month,
      () => filters.value.year,
    ],
    () => {
      fetchLeaveRequests(true);
    },
  );
  const getInitials = (name) => {
    if (!name) return "";

    return name
      .trim()
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };
</script>
